import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, RefreshCw, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { StatusChip, type StatusTone } from '../abf/components/StatusChip';
import { ViewportToast, type ActionFeedbackState } from '../abf/components/ActionFeedback';
import { DecisionBar, Header, Note, SmallButton, SummaryGrid } from '../abf/onboarding/review/contractReviewStyles';
import { Button, INK } from '../abf/onboarding/review/reviewStyles';
import { Panel, Shell, Table, Toolbar, words } from '../abf/board/boardShared';
import { DateField } from '../../components/shared/DateField';
import { loan360Path } from '../../utils/loanNavigation';
import { assignPcOwner, getPcWorkQueue, pcErrorMessage, triagePcItems } from './api';
import { pcActionPath, pcHomePath } from './routes';
import type { PcAttentionItem, PcWorkQueue } from './types';

/** F-PCP-017: the inline owner picker takes the toolbar's dark styling; the native popup inherits the colours. */
const OwnerSelect = styled.select`
  margin-top: 4px; padding: 5px 8px; border: 1px solid ${INK.lineStrong}; border-radius: 7px;
  background: ${INK.well}; color: ${INK.body}; font: inherit; font-size: 12px; max-width: 220px;
  option { background: ${INK.well}; color: ${INK.body}; }
`;
/** F-PCP-016: the item key is support detail — shown on hover of its row, copyable, never the first thing read. */
const ItemKey = styled.code`
  visibility: hidden; font-size: 11px; color: ${INK.faint}; tr:hover & { visibility: visible; }
`;
/** F-PCP-018: the triage form lives in the decision bar, where the click happened, not under a long table. */
const BarStack = styled.div`display: grid; gap: 10px; width: 100%;`;
/** F-PCP-050: triage state is its own column, so a dismissed or snoozed row is read at a glance; the next action
 *  column has one width, so its buttons line up on one line. */
const stateTone = (state: string): StatusTone => state === 'DISMISSED' ? 'neutral' : state === 'SNOOZED' ? 'progress' : state === 'ACKNOWLEDGED' ? 'done' : 'neutral';
const NextActionCell = styled.td`width: 232px; min-width: 232px; button { width: 100%; white-space: nowrap; justify-content: space-between; }`;
const BarRow = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;`;
/** F-PCP-049: inside the Portfolio Management hub the page sits in a card that clips overflow, which defeats a sticky
 *  footer. The bar is pinned to the window instead, aligned to the queue's own edges, and the queue leaves room for it. */
const Pinned = styled.div`position: fixed; bottom: 12px; z-index: 60; > footer { margin-top: 0; }`;
function usePinnedBar(ready: boolean) {
  const shellRef = useRef<HTMLElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  // Positioned straight on the DOM, coalesced to one frame: no React state, so measuring never re-renders the grid.
  useLayoutEffect(() => {
    let frame = 0;
    const place = () => {
      frame = 0;
      const shell = shellRef.current, bar = barRef.current;
      if (!shell || !bar) return;
      const rect = shell.getBoundingClientRect();
      const style = getComputedStyle(shell);
      const left = rect.left + parseFloat(style.paddingLeft);
      const width = rect.width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      bar.style.left = `${Math.round(left)}px`;
      bar.style.width = `${Math.round(width)}px`;
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(place); };
    place();
    const observer = new ResizeObserver(schedule);
    if (shellRef.current) observer.observe(shellRef.current);
    window.addEventListener('resize', schedule);
    return () => { observer.disconnect(); window.removeEventListener('resize', schedule); if (frame) window.cancelAnimationFrame(frame); };
  }, [ready]);
  return { shellRef, barRef };
}


/**
 * The private credit work queue (PC workflow parity, slice 1): every attention item across facilities,
 * assignable, SLA against the record's own dates, bulk triage, deep links into Loan 360. The ABF queue's
 * semantics for a loan subject.
 */

const severityTone = (item: Pick<PcAttentionItem, 'severity' | 'sla' | 'triageState'>): StatusTone =>
  item.triageState !== 'OPEN' ? 'neutral' : item.severity === 'CRITICAL' || item.sla === 'ESCALATED' ? 'critical'
    : item.severity === 'WARNING' || item.sla === 'OVERDUE' || item.sla === 'DUE_SOON' ? 'warning' : 'progress';
const slaLabel = (item: PcAttentionItem) => item.sla === 'UNASSIGNED' ? 'no due date'
  : `${words(item.sla)}${item.dueDate ? ` · due ${item.dueDate}` : ''}${item.dueBasis ? ` (${words(item.dueBasis)})` : ''}`;

const PrivateCreditWorkQueuePage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [queue, setQueue] = useState<PcWorkQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const [triage, setTriage] = useState<null | { action: 'ACKNOWLEDGE' | 'SNOOZE' | 'DISMISS' | 'REOPEN' }>(null);
  const [reason, setReason] = useState('');
  const [snoozeUntil, setSnoozeUntil] = useState('');
  const [assignKey, setAssignKey] = useState<string | null>(null);
  const [owner, setOwner] = useState('');
  const filters = useMemo(() => ({
    workflow: params.get('workflow') || undefined, severity: params.get('severity') || undefined,
    loanId: params.get('loanId') ? Number(params.get('loanId')) : undefined,
    mine: params.get('mine') === 'true', includeTriaged: params.get('includeTriaged') === 'true',
  }), [params]);
  const setFilter = (key: string, value: string) => { const next = new URLSearchParams(params); if (value) next.set(key, value); else next.delete(key); setParams(next, { replace: true }); };
  const load = useCallback(async () => {
    try { setQueue(await getPcWorkQueue(filters)); setError(null); }
    catch (failure) { setError(pcErrorMessage(failure, 'The work queue could not be loaded.')); }
  }, [filters]);
  useEffect(() => { void load(); }, [load]);
  const { shellRef, barRef } = usePinnedBar(queue !== null);
  const act = async (key: string, operation: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try { await operation(); setFeedback({ tone: 'success', message: success }); setTriage(null); setReason(''); setAssignKey(null); setSelected(new Set()); await load(); }
    catch (failure) { setFeedback({ tone: 'error', message: pcErrorMessage(failure, 'The action was refused.') }); }
    finally { setBusy(null); }
  };
  if (!queue) return <Shell ref={shellRef}><Note role={error ? 'alert' : 'status'}><p>{error || 'Loading the work queue…'}</p></Note></Shell>;
  const s = queue.summary;
  const lowSelected = queue.items.filter(i => selected.has(i.key) && i.severity !== 'CRITICAL').length;
  const loanFilterName = filters.loanId ? queue.items.find(i => i.loanId === filters.loanId)?.borrowerName || `facility ${filters.loanId}` : null;

  return <Shell ref={shellRef} style={{ paddingBottom: 160 }}>
    <Header>
      {!embedded && <Link className="back" to={pcHomePath()}><ArrowLeft size={14} /> Private credit</Link>}
      <div className="heading">
        <div>
          <div className="eyebrow">Private credit · work queue</div>
          <h1>Every attention item, routed</h1>
          <div className="meta"><span>Business date {queue.businessDate}</span><span>SLA against the record's own dates where it states one; escalation on breach to the operations lead</span></div>
        </div>
        <div className="actions">
          <SmallButton type="button" onClick={() => navigate(pcHomePath('overview'))}><LayoutGrid size={14} /> Board</SmallButton>
          <SmallButton type="button" onClick={() => void load()} disabled={busy !== null}><RefreshCw size={14} /> Refresh</SmallButton>
        </div>
      </div>
    </Header>

    <SummaryGrid aria-label="Queue summary" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
      <div><span>Open</span><strong>{s.open}</strong></div>
      <div><span>Mine</span><strong>{s.mine}</strong></div>
      <div><span>Overdue or escalated</span><strong style={{ color: s.overdue ? '#f8a5a5' : undefined }}>{s.overdue}</strong></div>
      <div><span>Critical</span><strong>{s.bySeverity.CRITICAL || 0}</strong></div>
      <div><span>By workflow</span><strong style={{ fontSize: 12 }}>{Object.entries(s.byWorkflow).map(([k, v]) => `${words(k)} ${v}`).join(' · ') || '—'}</strong></div>
    </SummaryGrid>

    <Toolbar>
      <label>Workflow <select value={filters.workflow || ''} onChange={e => setFilter('workflow', e.target.value)}><option value="">all</option>{queue.workflows.map(w => <option key={w} value={w}>{words(w)}</option>)}</select></label>
      <label>Severity <select value={filters.severity || ''} onChange={e => setFilter('severity', e.target.value)}><option value="">all</option><option value="CRITICAL">critical</option><option value="WARNING">warning</option><option value="INFO">info</option></select></label>
      <label><input type="checkbox" checked={filters.mine} onChange={e => setFilter('mine', e.target.checked ? 'true' : '')} /> Mine</label>
      <label><input type="checkbox" checked={filters.includeTriaged} onChange={e => setFilter('includeTriaged', e.target.checked ? 'true' : '')} /> Show acknowledged, snoozed and dismissed</label>
      {filters.loanId && <SmallButton type="button" onClick={() => setFilter('loanId', '')}>{loanFilterName} ✕</SmallButton>}
      {filters.includeTriaged && <span style={{ color: INK.muted, fontSize: 13 }}>{queue.items.length} shown · {queue.items.filter(i => i.triageState !== 'OPEN').length} triaged</span>}
    </Toolbar>

    {error && <Note role="alert">{error}</Note>}

    <Table>
      <thead><tr><th /><th>Item</th><th>Facility</th><th>Workflow</th><th>Severity</th><th>Status</th><th>SLA</th><th>Owner</th><th>Next action</th></tr></thead>
      <tbody>
        {queue.items.map(i => <tr key={i.key} data-critical={i.severity === 'CRITICAL' && i.triageState === 'OPEN'} data-mine={i.mine}>
          <td><input type="checkbox" checked={selected.has(i.key)} onChange={e => setSelected(prev => { const n = new Set(prev); if (e.target.checked) n.add(i.key); else n.delete(i.key); return n; })} /></td>
          <td>{i.text}{i.blocking && <> <StatusChip tone="critical" label="Blocks the facility" /></>}{i.escalatedAt && <><br /><small style={{ color: '#f8a5a5' }}>escalated to {i.escalatedTo || 'the operations lead'}</small></>}<br /><ItemKey title="Item key (support reference)">{i.key}</ItemKey></td>
          <td><Link to={loan360Path('PRIVATE_CREDIT', i.loanId)}>{i.borrowerName || i.loanCode}</Link><br /><small className="muted">{i.loanCode}</small></td>
          <td>{words(i.workflow)}</td>
          <td><StatusChip tone={severityTone(i)} label={words(i.severity)} /></td>
          <td>{i.triageState === 'OPEN' ? <span className="muted">Open</span> : <><StatusChip tone={stateTone(i.triageState)} label={words(i.triageState)} />
            <br /><small className="muted">{i.snoozedUntil ? `until ${i.snoozedUntil} · ` : ''}{i.triagedBy}{i.triageReason ? `: ${i.triageReason}` : ''}</small></>}</td>
          <td>{slaLabel(i)}</td>
          <td>{i.owner || '—'}
            {assignKey === i.key ? <div><OwnerSelect value={owner} onChange={e => setOwner(e.target.value)} autoFocus><option value="">choose</option>{queue.owners.map(o => <option key={o} value={o}>{o}</option>)}</OwnerSelect> <SmallButton type="button" disabled={!owner || busy !== null} onClick={() => void act('assign', () => assignPcOwner(i.key, owner), `Assigned to ${owner}.`)}>Assign</SmallButton> <SmallButton type="button" onClick={() => setAssignKey(null)}>Cancel</SmallButton></div>
              : <><br /><button type="button" style={{ background: 'none', border: 0, color: INK.accentSoft, cursor: 'pointer', padding: 0, font: 'inherit', fontSize: 12 }} onClick={() => { setAssignKey(i.key); setOwner(user?.username || ''); }}>assign</button></>}
          </td>
          <NextActionCell><SmallButton type="button" disabled={i.actorMayAct === false} title={i.actorMayAct === false ? `Requires ${i.nextAction.requiredRoles.join(', ')}` : undefined}
            onClick={() => navigate(pcActionPath(i.loanId, i.nextAction.workspace, i.nextAction.query))}>{i.nextAction.label} →</SmallButton>
            {i.actorMayAct === false && <><br /><small className="muted">needs {i.nextAction.requiredRoles.map(r => words(r)).join(', ')}</small></>}</NextActionCell>
        </tr>)}
        {queue.items.length === 0 && <tr><td colSpan={9} className="muted">Nothing needs attention under these filters.</td></tr>}
      </tbody>
    </Table>


    {createPortal(<Pinned ref={barRef}><DecisionBar aria-label="Queue actions"><BarStack>
      {triage && <Panel role="dialog" aria-label="Triage">
      <h3>{words(triage.action)} {selected.size} item{selected.size === 1 ? '' : 's'}</h3>
      <p>{triage.action === 'DISMISS' ? 'Only warning and info items can be dismissed; a critical item is resolved by its workflow.' : triage.action === 'SNOOZE' ? 'Snoozed items return to the queue on the date.' : triage.action === 'REOPEN' ? 'Reopen puts the items back in the queue.' : 'Acknowledged items stay visible under "show triaged" with your reason.'}</p>
      {triage.action === 'SNOOZE' && <label>Until<DateField value={snoozeUntil} onChange={setSnoozeUntil} /></label>}
      {triage.action !== 'REOPEN' && <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Why" autoFocus={triage.action !== 'SNOOZE'} />}
      <div className="row">
        <Button $variant="primary" type="button" disabled={busy !== null || (triage.action !== 'REOPEN' && !reason.trim()) || (triage.action === 'SNOOZE' && !snoozeUntil)}
          onClick={() => void act('triage', async () => { const r = await triagePcItems(Array.from(selected), triage.action, reason.trim(), snoozeUntil || undefined); if (r.applied === 0) throw new Error(r.results.map(x => `${x.key}: ${x.reason || x.outcome}`).join('; ')); return r; }, `${words(triage.action)} applied.`)}><ShieldCheck size={14} /> Apply</Button>
        <Button $variant="quiet" type="button" onClick={() => setTriage(null)}>Cancel</Button>
      </div>
    </Panel>}
      <BarRow>
      <div><strong>{selected.size ? `${selected.size} selected` : 'Select items to triage'}</strong><span>{selected.size ? `${lowSelected} can be dismissed; critical items are resolved in their workflow` : 'Acknowledge, snooze or dismiss low-severity items in bulk; assign owners per item'}</span></div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button $variant="quiet" type="button" disabled={!selected.size} onClick={() => setTriage({ action: 'ACKNOWLEDGE' })}>Acknowledge</Button>
        <Button $variant="quiet" type="button" disabled={!selected.size} onClick={() => setTriage({ action: 'SNOOZE' })}>Snooze</Button>
        <Button $variant="quiet" type="button" disabled={!lowSelected} onClick={() => setTriage({ action: 'DISMISS' })}>Dismiss</Button>
        <Button $variant="quiet" type="button" disabled={!selected.size} onClick={() => setTriage({ action: 'REOPEN' })}>Reopen</Button>
      </div>
      </BarRow>
    </BarStack></DecisionBar></Pinned>, document.body)}
    <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
  </Shell>;
};

export default PrivateCreditWorkQueuePage;
